import { FilePdfOutlined } from '@ant-design/icons'
import { colors } from '@lmc/common/constants'
import type { Home } from '@lmc/ts-common'
import {
  Banner,
  Button,
  Card,
  Chevron,
  Dropdown,
  EmptyState,
  FilterContainer,
  NoDataIcon,
  PageHeader,
  RangePicker,
  Spinner
} from '@lmc/ui-kit'
import ExternalLink from 'components/icons/ExternalLink'
import dayjs from 'dayjs'
import usePdfExport from 'hooks/usePdfExport'
import { NAMESPACE } from 'locale'
import type React from 'react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { AdministrationRecordDrawer } from '../componentsV2/AdministrationRecordDrawer'
import AddStockModal from '../componentsV2/modalsV2/AddStockModal'
import AdministrationModal from '../componentsV2/modalsV2/AdministrationModal'
import DisposalModal from '../componentsV2/modalsV2/DisposalModal'
import StockCheckModal from '../componentsV2/modalsV2/StockCheckModal'
import PageWrapper from '../componentsV2/PageWrapper'
import { StockRecordDrawer } from '../componentsV2/StockRecordDrawer/StockRecordDrawer'
import { useCurrentStock } from '../hooks/useCurrentStock'
import { useResidentMedications } from '../hooks/useResidentMedications'
import { useStockRecords } from '../hooks/useStockRecords'
import { MedicationStockReason, type StockRecord } from '../types'
import MedStockChart from './MedStockChart'
import { StockRecordsTable } from './StockRecordsTable/StockRecordsTable'

const EMARMedicationStockScreenV2 = () => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const navigate = useNavigate()
  const { id: residentId, medication_id: medicationId } = useParams()
  const [searchParams] = useSearchParams()
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const startDateParam = searchParams.get('startDate')
  const endDateParam = searchParams.get('endDate')

  const [startDate, setStartDate] = useState(
    startDateParam
      ? dayjs(startDateParam).startOf('day')
      : dayjs().subtract(14, 'days').startOf('day')
  )
  const [endDate, setEndDate] = useState(
    endDateParam ? dayjs(endDateParam).endOf('day') : dayjs().endOf('day')
  )

  const { data: medicationsData, isLoading: isLoadingMedication } =
    useResidentMedications({
      homeId: home?.id || '',
      residentId: residentId || '',
      residentMedicationId: medicationId
    })

  const {
    records,
    chartData: stockChartData,
    isLoading: isLoadingStockRecords
  } = useStockRecords({
    medicationId: medicationId || '',
    startDate,
    endDate,
    includeChartData: true
  })

  const { currentStock, isLoading: isLoadingCurrentStock } = useCurrentStock({
    residentMedicationId: medicationId || '',
    residentId: residentId || '',
    enabled: !!medicationId && !!residentId
  })

  const isLoading =
    isLoadingMedication || isLoadingStockRecords || isLoadingCurrentStock

  const allMedications = [
    ...(medicationsData?.scheduledMedications || []),
    ...(medicationsData?.prnMedications || []),
    ...(medicationsData?.discontinuedMedications || [])
  ]

  const medicationData = allMedications.find(
    (med) => med.residentMedicationId === medicationId
  )

  const [stockCheckModalOpen, setStockCheckModalOpen] = useState(false)
  const [addStockModalOpen, setAddStockModalOpen] = useState(false)
  const [disposalModalOpen, setDisposalModalOpen] = useState(false)
  const [administrationModalOpen, setAdministrationModalOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [administrationDrawerOpen, setAdministrationDrawerOpen] =
    useState(false)
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null)
  const [editingRecord, setEditingRecord] = useState<StockRecord | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [showAllRecords, setShowAllRecords] = useState(false)
  const pdfContentRef = useRef<HTMLDivElement>(null)

  const sortedRecords = [...records].sort(
    (a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf()
  )

  if (isLoading) {
    return (
      <PageWrapper>
        <Spinner style={{ marginTop: '80px' }} />
      </PageWrapper>
    )
  }

  const expectedStock = currentStock?.currentStockLevel ?? 0

  const medication = medicationData
    ? {
        id: medicationData.residentMedicationId,
        name: medicationData.medicationName,
        stockQuantityType: medicationData.unit,
        quantityType: medicationData.unit,
        currentStock: medicationData.currentStockLevel,
        expectedStock,
        lowStockThreshold: medicationData.stockAlertLevel
      }
    : {
        id: medicationId || '',
        name: '',
        stockQuantityType: currentStock?.unit || '',
        quantityType: currentStock?.unit || '',
        currentStock: currentStock?.currentStockLevel ?? 0,
        expectedStock,
        lowStockThreshold: 0
      }

  const showAlert = medication.currentStock <= medication.lowStockThreshold

  const handleDateChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(dates[0])
      setEndDate(dates[1])
    } else {
      setStartDate(dayjs().subtract(14, 'days'))
      setEndDate(dayjs())
    }
  }

  const handleStockAction = (action: string) => {
    if (action === 'Stock Check') {
      setStockCheckModalOpen(true)
    } else if (action === 'Add Stock') {
      setAddStockModalOpen(true)
    } else if (action === 'Disposal') {
      setDisposalModalOpen(true)
    }
  }

  const handleStockCheckSuccess = () => {
    setStockCheckModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleStockCheckCancel = () => {
    setStockCheckModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleAddStockSuccess = () => {
    setAddStockModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleAddStockCancel = () => {
    setAddStockModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleDisposalSuccess = () => {
    setDisposalModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleDisposalCancel = () => {
    setDisposalModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const pdfTitle = `${medication.name}-Stock-${startDate.format('DD-MM-YYYY')}-${endDate.format('DD-MM-YYYY')}`
  const handleDownloadPdfBase = usePdfExport(
    pdfContentRef as React.RefObject<HTMLInputElement>,
    pdfTitle,
    setIsDownloading
  )

  const handleDownloadPdf = () => {
    setShowAllRecords(true)
    setTimeout(() => {
      handleDownloadPdfBase()
      setTimeout(() => {
        setShowAllRecords(false)
      }, 1000)
    }, 100)
  }

  const handleReadMore = () => {
    window.open(
      'https://support.logmycare.co.uk/en/collections/2957232-stock',
      '_blank',
      'noopener,noreferrer'
    )
  }

  const handleViewRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setDrawerOpen(true)
  }

  const handleEditRecord = (recordId: string) => {
    const record = sortedRecords.find((r) => r.id === recordId)
    if (!record) return

    setIsEditMode(true)
    setEditingRecord(record)

    switch (record.action) {
      case MedicationStockReason.STOCK_COUNT:
      case MedicationStockReason.RECONCILIATION:
      case MedicationStockReason.INITIAL_STOCK:
        setStockCheckModalOpen(true)
        break
      case MedicationStockReason.RESTOCK:
        setAddStockModalOpen(true)
        break
      case MedicationStockReason.DISPOSAL:
        setDisposalModalOpen(true)
        break
    }
  }

  const handleViewAdministrationRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setAdministrationDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedRecordId(null)
  }

  const handleCloseAdministrationDrawer = () => {
    setAdministrationDrawerOpen(false)
    setSelectedRecordId(null)
  }

  const handleEditFromDrawer = (
    record: StockRecord,
    actionType?: MedicationStockReason
  ) => {
    if (record.type === 'administration') {
      setIsEditMode(true)
      setEditingRecord(record)
      setAdministrationModalOpen(true)
      return
    }

    if (!actionType) return

    setIsEditMode(true)
    setEditingRecord(record)

    switch (actionType) {
      case MedicationStockReason.STOCK_COUNT:
      case MedicationStockReason.RECONCILIATION:
      case MedicationStockReason.INITIAL_STOCK:
        setStockCheckModalOpen(true)
        break
      case MedicationStockReason.RESTOCK:
        setAddStockModalOpen(true)
        break
      case MedicationStockReason.DISPOSAL:
        setDisposalModalOpen(true)
        break
      default:
        break
    }
  }

  const handleEditAdministrationCancel = () => {
    setAdministrationModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleEditAdministrationSuccess = () => {
    setAdministrationModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
    setAdministrationDrawerOpen(false)
  }

  const selectedRecord = sortedRecords.find((r) => r.id === selectedRecordId)

  const stockMenuItems = [
    {
      key: 'stock-check',
      label: t('STOCK_CHECK'),
      onClick: () => handleStockAction('Stock Check'),
      'data-testid': 'emar-stock-menu-stock-check'
    },
    {
      key: 'add-stock',
      label: t('ADD_STOCK'),
      onClick: () => handleStockAction('Add Stock'),
      'data-testid': 'emar-stock-menu-add-stock'
    },
    {
      key: 'disposal',
      label: t('DISPOSAL'),
      onClick: () => handleStockAction('Disposal'),
      'data-testid': 'emar-stock-menu-disposal'
    }
  ]

  const handleBreadcrumbClick = () => {
    navigate(`/residents/${residentId}/emar/stock`)
  }

  return (
    <div ref={pdfContentRef}>
      <PageHeader
        sticky
        title={t('STOCK_MEDICATION_NAME', { medicationName: medication.name })}
        breadcrumb={{
          text: t('STOCK'),
          onClick: handleBreadcrumbClick
        }}
        testId="med-stock-header-v2"
      >
        <HeaderActions>
          <ReadMoreLink
            onClick={handleReadMore}
            data-testid="read-more-link-v2"
          >
            {t('READ_MORE')} <ExternalLink />
          </ReadMoreLink>

          <PdfExportContainer data-testid="download-pdf-btn-v2">
            <Button
              onClick={handleDownloadPdf}
              variant="default"
              isLoading={isDownloading}
              style={{ whiteSpace: 'nowrap' }}
              disabled={isDownloading}
            >
              <FilePdfOutlined />
              {t('DOWNLOAD_PDF')}
            </Button>
          </PdfExportContainer>

          <Dropdown
            menu={{ items: stockMenuItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button
              variant="primary"
              data-testid="manage-stock-btn-v2"
              style={{ whiteSpace: 'nowrap' }}
            >
              {t('MANAGE_STOCK')}
              <Chevron direction="down" />
            </Button>
          </Dropdown>
        </HeaderActions>
      </PageHeader>
      <PageWrapper testId="emar-medication-stock-screen-v2">
        {!medicationData ? (
          <Card>
            <EmptyState
              icon={<NoDataIcon />}
              title={t('MEDICATION_NOT_FOUND')}
            />
          </Card>
        ) : (
          <>
            {showAlert && (
              <Banner
                color={colors.red}
                text={t('LOW_STOCK_ALERT')}
                data-testid="emar-stock-alert-banner-v2"
                style={{ marginTop: 0 }} // UGH.
              />
            )}

            <FilterContainer title="">
              <RangePicker
                defaultValue={[startDate, endDate]}
                onChange={handleDateChange}
                format="DD-MM-YYYY"
                data-testid="date-range-picker-v2"
              />
            </FilterContainer>

            <ChartCard title={t('STOCK_LEVEL_CHART')}>
              <MedStockChart
                medication={medication}
                records={sortedRecords}
                chartData={stockChartData || []}
                startDate={startDate}
                endDate={endDate}
                testId="emar-medication-stock-screen-v2"
              />
            </ChartCard>

            <StockRecordsTable
              records={sortedRecords}
              onViewRecord={handleViewRecord}
              onEditRecord={handleEditRecord}
              onViewAdministrationRecord={handleViewAdministrationRecord}
              showAllRecords={showAllRecords}
            />
          </>
        )}

        {stockCheckModalOpen && (
          <StockCheckModal
            open={stockCheckModalOpen}
            medicationName={medication.name}
            expectedStock={expectedStock}
            residentId={residentId || ''}
            residentMedicationId={medicationId || ''}
            unit={medicationData?.unit || currentStock?.unit}
            onCancel={handleStockCheckCancel}
            onSuccess={handleStockCheckSuccess}
            editMode={isEditMode}
            record={isEditMode ? editingRecord || undefined : undefined}
            trackStock={medicationData?.trackStock}
          />
        )}
        {addStockModalOpen && (
          <AddStockModal
            open={addStockModalOpen}
            medicationName={medication.name}
            expectedStock={expectedStock}
            residentId={residentId || ''}
            residentMedicationId={medicationId || ''}
            unit={medicationData?.unit || currentStock?.unit}
            onCancel={handleAddStockCancel}
            onSuccess={handleAddStockSuccess}
            editMode={isEditMode}
            record={isEditMode ? editingRecord || undefined : undefined}
          />
        )}
        {disposalModalOpen && (
          <DisposalModal
            open={disposalModalOpen}
            medicationName={medication.name}
            expectedStock={expectedStock}
            residentId={residentId || ''}
            residentMedicationId={medicationId || ''}
            unit={medicationData?.unit || currentStock?.unit}
            onCancel={handleDisposalCancel}
            onSuccess={handleDisposalSuccess}
            editMode={isEditMode}
            record={isEditMode ? editingRecord || undefined : undefined}
          />
        )}
        <StockRecordDrawer
          trackStock={medicationData?.trackStock}
          isVisible={drawerOpen}
          onClose={handleCloseDrawer}
          record={selectedRecord}
          medicationName={medication.name}
          controlledDrug={medicationData?.controlledDrug}
          onEdit={handleEditFromDrawer}
        />
        {administrationModalOpen && (
          <AdministrationModal
            open={administrationModalOpen}
            medicationName={medication.name}
            record={editingRecord || undefined}
            onCancel={handleEditAdministrationCancel}
            onSuccess={handleEditAdministrationSuccess}
          />
        )}
        <AdministrationRecordDrawer
          isVisible={administrationDrawerOpen}
          onClose={handleCloseAdministrationDrawer}
          record={selectedRecord}
          medicationName={medication.name}
          controlledDrug={medicationData?.controlledDrug}
          onEdit={handleEditFromDrawer}
        />
      </PageWrapper>
    </div>
  )
}

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const ReadMoreLink = styled.a`
  color: ${colors.primary};
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  white-space: nowrap;
  align-items: center;
  gap: 4px;
  transition: color 0.2s ease;

  &:hover {
    color: ${colors.primary};
    text-decoration: underline;
    opacity: 0.8;
  }
`

const PdfExportContainer = styled.div`
  white-space: nowrap;

  ${({ theme }) => theme.mediaQueries.tabletDown} {
    display: none;
  }
`

const ChartCard = styled.div`
  border-radius: 16px;
  background-color: ${({ theme }) => theme.color.neutral.color11};
  padding: 24px 24px 0 16px;
`

const PdfContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export default EMARMedicationStockScreenV2
